import { useUnsavedChanges } from '@/hooks/useUnsavedChanges';
import { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import {
  workOrders as api, offers as offersApi, fieldWorks as fieldWorksApi,
  type WorkOrder, type WorkOrderInput, type WorkOrderMeta, type WorkOrderPriceVariant, type OrderChannelCode,
  type FieldWorkKindCode, type FieldWorkMeta,
} from '@/api/endpoints';
import type { Client, Offer } from '@/types';
import { hasPermission } from '@/types';
import { useAuth } from '@/auth/AuthContext';
import { Icon } from './Icon';
import { SearchableSelect } from './SearchableSelect';
import { formatCurrency } from '@/lib/money';
import { CoordsInput } from './CoordsInput';
import { PunktyPracMapa } from './PunktyPracMapa';

interface Props {
  open: boolean;
  initial?: WorkOrder | null;
  clients: Client[];
  meta: WorkOrderMeta | null;
  /** Klient wybrany z góry (np. z karty klienta) — pole startuje wypełnione. */
  presetClientId?: number | null;
  onClose: () => void;
  onSaved: (order: WorkOrder) => void;
}

function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Wiersz edytora wariantów. Kwota jako tekst — puste pole ma zostać puste, a nie zamienić się w 0. */
interface VariantRow {
  label: string;
  netAmount: string;
  billed: boolean;
}

/** Praca terenowa zakładana razem ze zleceniem — tylko pola, które da się ustalić przy telefonie. */
interface WorkRow {
  title: string;
  kind: FieldWorkKindCode;
  wellLabel: string;
  locationText: string;
  plannedDate: string;
  latitude: number | null;
  longitude: number | null;
}

const EMPTY_WORK: WorkRow = {
  title: '', kind: 'REGENERACJA', wellLabel: '', locationText: '', plannedDate: '',
  latitude: null, longitude: null,
};

/** Podpowiedzi w kolejnych wierszach — tak oferty na regenerację zapisują etapy. */
const VARIANT_HINTS = [
  'np. Etap I – regeneracja mechaniczna',
  'np. Etap II – regeneracja chemiczna (gdy etap I nie przyniesie efektu)',
  'np. Etap III – …',
];

/** Pusta albo niepoprawna kwota to „brak kwoty", nie zero. */
function parseAmount(s: string): number | null {
  if (s.trim() === '') return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

const EMPTY: WorkOrderInput = {
  clientId: null, title: '', orderDate: '', receivedVia: 'PHONE',
  clientOrderRef: '', description: '', deadline: '', netAmount: null, notes: '',
  sourceOfferId: null, sourceOfferRef: '', receivedById: null,
};

/**
 * Rejestracja zlecenia na roboty.
 *
 * Formularz jest zbudowany pod przypadek, który go wywołał: „zlecenie ad-hoc, ugadane na telefon".
 * Wymagane są DWA pola — klient i opis. Data podstawia się na dziś, kanał na „telefonicznie",
 * numer nadaje system. Oferta, umowa klienta, termin, kwota i skan są opcjonalne i schowane
 * pod „Szczegóły", żeby nie sugerowały, że trzeba je wypełnić.
 *
 * Oferta: z portalu (lista ofert klienta) ALBO sam numer z pisma — kontrahenci z importu ewidencji
 * mają oferty wystawione poza portalem i lista była dla nich pusta. Cena: jedna kwota ALBO warianty
 * /etapy z oferty; wtedy wartość zlecenia to suma wariantów zaznaczonych jako rozliczane (V174).
 */
export function ZlecenieFormModal({ open, initial, clients, meta, presetClientId, onClose, onSaved }: Props) {
  const { user } = useAuth();
  const canSeePricing = hasPermission(user, 'PRICING_VIEW');
  const canSeeOffers = hasPermission(user, 'OFFER_VIEW');
  const canAddWorks = hasPermission(user, 'FIELDWORK_EDIT');

  const [data, setData] = useState<WorkOrderInput>(EMPTY);
  const [variants, setVariants] = useState<VariantRow[]>([]);
  // Prace zakładane od razu ze zleceniem — tylko przy rejestracji. Edycja zlecenia prac nie
  // rusza: mają własny formularz i karta zlecenia linkuje do nich osobno.
  const [works, setWorks] = useState<WorkRow[]>([]);
  /** Praca, której kliknięcie w mapę ustawia punkt. */
  const [activeWork, setActiveWork] = useState<number | null>(null);
  const [workMeta, setWorkMeta] = useState<FieldWorkMeta | null>(null);
  // null = jeszcze nie wczytane — odróżnia „wczytuję" od „klient nie ma ofert w portalu".
  const [offerOpts, setOfferOpts] = useState<Offer[] | null>(null);
  const [details, setDetails] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useLayoutEffect(() => {
    if (!open) return;
    resetDirty();
    setErr(null);
    setBusy(false);
    if (initial) {
      setData({
        clientId: initial.clientId,
        title: initial.title,
        orderDate: initial.orderDate,
        receivedVia: initial.receivedVia,
        clientOrderRef: initial.clientOrderRef ?? '',
        description: initial.description ?? '',
        deadline: initial.deadline ?? '',
        netAmount: initial.netAmount,
        notes: initial.notes ?? '',
        sourceOfferId: initial.sourceOfferId,
        sourceOfferRef: initial.sourceOfferRef ?? '',
        receivedById: initial.receivedById,
      });
      setVariants((initial.priceVariants ?? []).map(v => ({
        label: v.label, netAmount: String(v.netAmount), billed: v.billed,
      })));
      // Przy edycji od razu pokaż szczegóły, jeśli którykolwiek jest wypełniony — inaczej
      // użytkownik nie zobaczy danych, które sam wpisał.
      setDetails(!!(initial.clientOrderRef || initial.deadline || initial.netAmount
        || initial.priceVariants?.length || initial.sourceOfferId || initial.sourceOfferRef
        || initial.description || initial.notes));
    } else {
      setData({ ...EMPTY, orderDate: todayIso(), clientId: presetClientId ?? null });
      setVariants([]);
      setDetails(false);
    }
    setWorks([]);
    setActiveWork(null);
  }, [open, initial, presetClientId]);

  useEffect(() => {
    if (!open || initial || !canAddWorks || workMeta) return;
    fieldWorksApi.meta().then(setWorkMeta).catch(() => { /* rodzaje mają wartość domyślną */ });
  }, [open, initial, canAddWorks, workMeta]);

  useEffect(() => {
    if (!open || !data.clientId || !canSeeOffers) { setOfferOpts(null); return; }
    // Szybkie przełączanie klienta nie może wpuścić do listy ofert poprzedniego.
    let alive = true;
    setOfferOpts(null);
    offersApi.list(undefined, data.clientId)
      .then(list => { if (alive) setOfferOpts(list); })
      .catch(() => { if (alive) setOfferOpts([]); });
    return () => { alive = false; };
  }, [open, data.clientId, canSeeOffers]);

  const { requestClose, confirmation, dialogProps, resetDirty } = useUnsavedChanges({
    value: { data, variants, works }, onClose, open, busy,
  });

  // Stabilna referencja: mapa przebudowuje pinezki przy zmianie punktów, a nie przy każdym
  // znaku wpisanym w opis zlecenia.
  const mapPoints = useMemo(
    () => works.map((w, i) => ({ label: String(i + 1), lat: w.latitude, lng: w.longitude })),
    [works],
  );
  const client = clients.find(c => c.id === data.clientId);
  const clientPoint = client?.latitude != null && client?.longitude != null
    ? { lat: client.latitude, lng: client.longitude } : null;

  if (!open) return null;

  const set = <K extends keyof WorkOrderInput>(k: K) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setData(d => ({ ...d, [k]: e.target.value }));

  // ── Warianty ceny ─────────────────────────────────────────────────────────
  const setVariant = (i: number, patch: Partial<VariantRow>) =>
    setVariants(rows => rows.map((r, idx) => idx === i ? { ...r, ...patch } : r));

  // Ta sama reguła co na serwerze: suma rozliczanych, a gdy nic nie jest rozliczane — brak kwoty.
  const billedTotal = variants.filter(r => r.billed).reduce<number | null>((sum, r) => {
    const n = parseAmount(r.netAmount);
    return n == null ? sum : (sum ?? 0) + n;
  }, null);

  const splitIntoVariants = () => setVariants([
    // Kwota wpisana dotąd trafia do pierwszego etapu, żeby nie zniknęła po przełączeniu.
    { label: '', netAmount: data.netAmount != null ? String(data.netAmount) : '', billed: true },
    { label: '', netAmount: '', billed: false },
  ]);

  const removeVariant = (i: number) => {
    const rest = variants.filter((_, idx) => idx !== i);
    // Po usunięciu ostatniego wariantu wraca jedna kwota — z tym, co było rozliczane.
    if (rest.length === 0) setData(d => ({ ...d, netAmount: billedTotal }));
    setVariants(rest);
  };

  // ── Prace terenowe ────────────────────────────────────────────────────────
  const setWork = (i: number, patch: Partial<WorkRow>) =>
    setWorks(rows => rows.map((r, idx) => idx === i ? { ...r, ...patch } : r));

  const addWork = () => {
    // Nowa praca od razu czeka na punkt — „Dodaj pracę" i klik w mapę to jeden ruch.
    setActiveWork(works.length);
    setWorks(rows => [...rows, { ...EMPTY_WORK }]);
  };

  const removeWork = (i: number) => {
    setWorks(rows => rows.filter((_, idx) => idx !== i));
    setActiveWork(a => a == null || a === i ? null : (a > i ? a - 1 : a));
  };

  // ── Oferta ────────────────────────────────────────────────────────────────
  const offers = offerOpts ?? [];
  const hasPortalOffers = offers.length > 0 || !!data.sourceOfferId;
  // Podpięta oferta musi być opcją także zanim lista się wczyta — inaczej pole udaje puste.
  const linkedMissing = !!data.sourceOfferId && !offers.some(o => o.id === data.sourceOfferId);
  const offerHint = data.sourceOfferId ? 'Podpięcie nie zmienia statusu oferty.'
    : !data.clientId ? 'Po wybraniu klienta pojawią się tu jego oferty z portalu.'
    : offerOpts === null ? 'Wczytuję oferty klienta…'
    : offers.length === 0 ? 'Ten klient nie ma ofert w portalu — wpisz numer z pisma oferty.'
    : 'Wybierz ofertę z portalu albo wpisz numer oferty wystawionej poza portalem.';

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!data.clientId) { setErr('Wybierz klienta'); return; }
    if (!data.title?.trim()) { setErr('Opis zlecenia jest wymagany'); return; }

    // Wiersz całkiem pusty to przypadkowe „Dodaj wariant" — pomijamy go zamiast blokować zapis.
    const priceVariants: WorkOrderPriceVariant[] = [];
    for (const r of variants.filter(v => v.label.trim() || v.netAmount.trim())) {
      const amount = parseAmount(r.netAmount);
      if (!r.label.trim() || amount == null || amount < 0) {
        setErr('Każdy wariant ceny potrzebuje opisu i kwoty netto (0 lub więcej) — uzupełnij albo usuń wiersz');
        setDetails(true);
        return;
      }
      priceVariants.push({ label: r.label.trim(), netAmount: amount, billed: r.billed });
    }

    // Praca całkiem pusta (bez opisu, studni i punktu) to przypadkowe „Dodaj pracę" — pomijamy.
    const filledWorks = works.filter(w =>
      w.title.trim() || w.wellLabel.trim() || w.locationText.trim() || w.latitude != null);
    const untitled = filledWorks.findIndex(w => !w.title.trim());
    if (untitled >= 0) {
      setErr(`Praca ${works.indexOf(filledWorks[untitled]) + 1}: podaj opis prac albo usuń wiersz`);
      return;
    }
    if (filledWorks.some(w => (w.latitude == null) !== (w.longitude == null))) {
      setErr('Każda praca potrzebuje obu współrzędnych albo żadnej');
      return;
    }

    const payload: WorkOrderInput = {
      ...data,
      title: data.title.trim(),
      orderDate: data.orderDate || todayIso(),
      clientOrderRef: data.clientOrderRef?.trim() || null,
      description: data.description?.trim() || null,
      deadline: data.deadline || null,
      notes: data.notes?.trim() || null,
      // Pola chronione jadą tylko od tego, kto je widzi — brak pola serwer czyta jako „bez zmian".
      netAmount: canSeePricing ? (priceVariants.length ? null : (data.netAmount ?? null)) : undefined,
      priceVariants: canSeePricing ? priceVariants : undefined,
      sourceOfferId: canSeeOffers ? (data.sourceOfferId ?? null) : undefined,
      sourceOfferRef: canSeeOffers ? (data.sourceOfferRef?.trim() || null) : undefined,
      works: !initial && canAddWorks && filledWorks.length
        ? filledWorks.map(w => ({
          title: w.title.trim(),
          kind: w.kind,
          wellLabel: w.wellLabel.trim() || null,
          locationText: w.locationText.trim() || null,
          plannedDate: w.plannedDate || null,
          latitude: w.latitude,
          longitude: w.longitude,
          // Termin z umowy pracy = termin zlecenia; alerty „po terminie" działają od pierwszego dnia.
          contractDeadline: data.deadline || null,
        }))
        : undefined,
    };

    setBusy(true);
    const op = initial ? api.update(initial.id, payload) : api.create(payload);
    op.then(o => { onSaved(o); onClose(); })
      .catch(e2 => setErr(e2?.message ?? String(e2)))
      .finally(() => setBusy(false));
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={requestClose} {...dialogProps}>
      {confirmation}
      <form onClick={e => e.stopPropagation()} onSubmit={submit}
            className={`bg-white rounded-lg shadow-xl w-full ${works.length ? 'max-w-[860px]' : 'max-w-[680px]'} max-h-[92vh] overflow-auto`}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-ink-100 sticky top-0 bg-white z-10">
          <h2 className="text-[16px] font-semibold text-ink-900">
            {initial ? `Edytuj zlecenie ${initial.number}` : 'Nowe zlecenie – praca w terenie'}
          </h2>
          <button type="button" onClick={requestClose} className="btn btn-ghost btn-icon text-ink-400 hover:text-ink-700">
            <Icon name="x"/>
          </button>
        </div>

        <div className="p-5 grid grid-cols-2 gap-3">
          {!initial && (
            <div className="col-span-2 text-[12px] text-ink-500 bg-ink-50 border border-ink-100 rounded p-2.5">
              Numer nada system. Do zarejestrowania zlecenia z telefonu wystarczą dwa pola poniżej —
              resztę można uzupełnić później albo wcale.
            </div>
          )}

          <label className="field col-span-2">
            <span className="field-label">Klient *</span>
            {/* Zmiana kontrahenta odpina ofertę z portalu — należała do poprzedniego i serwer
                i tak by ją odrzucił. Numer z pisma zostaje: to tekst wpisany ręcznie. */}
            <SearchableSelect value={data.clientId ? String(data.clientId) : ''}
                              onChange={v => setData(d => {
                                const clientId = v ? Number(v) : null;
                                return clientId === d.clientId ? d : { ...d, clientId, sourceOfferId: null };
                              })}>
              <option value="">— wybierz —</option>
              {clients.map(c => <option key={c.id} value={String(c.id)}>{c.name}</option>)}
            </SearchableSelect>
          </label>

          <label className="field col-span-2">
            <span className="field-label">Czego dotyczy *</span>
            <input value={data.title ?? ''} onChange={set('title')} className="input" autoFocus
                   placeholder="np. Regeneracja trzech studni na ujęciu w Trzebiatowie"/>
          </label>

          <label className="field">
            <span className="field-label">Data zlecenia</span>
            <input type="date" value={data.orderDate ?? ''} onChange={set('orderDate')} className="input"/>
          </label>
          <label className="field">
            <span className="field-label">Jak wpłynęło</span>
            <SearchableSelect value={data.receivedVia ?? 'PHONE'}
                              onChange={v => setData(d => ({ ...d, receivedVia: v as OrderChannelCode }))}>
              {(meta?.channels ?? []).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </SearchableSelect>
          </label>

          <div className="col-span-2">
            <button type="button" onClick={() => setDetails(v => !v)}
                    className="btn btn-ghost btn-sm text-ink-500">
              <Icon name={details ? 'chevron' : 'plus'} className="w-3.5 h-3.5"/>
              {details ? 'Ukryj szczegóły' : 'Szczegóły (termin, kwota, oferta, numer u klienta)'}
            </button>
          </div>

          {details && (
            <>
              <label className="field">
                <span className="field-label">Numer u klienta</span>
                <input value={data.clientOrderRef ?? ''} onChange={set('clientOrderRef')} className="input"
                       placeholder="np. ZAM/2026/118"/>
                <span className="text-[11px] text-ink-400 mt-0.5">
                  Oznaczenie z pisma klienta. Nasz numer nadaje system osobno.
                </span>
              </label>
              <label className="field">
                <span className="field-label">Termin realizacji</span>
                <input type="date" value={data.deadline ?? ''} onChange={set('deadline')} className="input"/>
              </label>

              {canSeeOffers && (
                <div className="field col-span-2">
                  <span className="field-label">Oferta</span>
                  <div className="grid grid-cols-2 gap-3">
                    {hasPortalOffers && (
                      <SearchableSelect value={data.sourceOfferId ? String(data.sourceOfferId) : ''} clearable
                                        onChange={v => setData(d => ({ ...d, sourceOfferId: v ? Number(v) : null }))}
                                        placeholder="— oferta z portalu —">
                        {linkedMissing && (
                          <option value={String(data.sourceOfferId)}>
                            {initial?.sourceOfferNumber ?? `oferta #${data.sourceOfferId}`}
                          </option>
                        )}
                        {offers.map(o => (
                          <option key={o.id} value={String(o.id)}>{o.number} — {o.title}</option>
                        ))}
                      </SearchableSelect>
                    )}
                    {!data.sourceOfferId && (
                      <input value={data.sourceOfferRef ?? ''} onChange={set('sourceOfferRef')} className="input"
                             maxLength={120} aria-label="Numer oferty spoza portalu"
                             placeholder={hasPortalOffers ? 'albo numer oferty spoza portalu' : 'numer oferty, np. 45/2026'}/>
                    )}
                  </div>
                  <span className="text-[11px] text-ink-400 mt-0.5">{offerHint}</span>
                </div>
              )}

              {canSeePricing && (
                <div className="field col-span-2">
                  <span className="field-label">Wartość netto (PLN)</span>
                  {variants.length === 0 ? (
                    <div className="flex items-center gap-3">
                      <input type="number" step="0.01" value={data.netAmount ?? ''} aria-label="Wartość netto (PLN)"
                             onChange={e => setData(d => ({ ...d, netAmount: e.target.value === '' ? null : Number(e.target.value) }))}
                             className="input font-mono max-w-[200px]" placeholder="0.00"/>
                      <button type="button" onClick={splitIntoVariants} className="btn btn-ghost btn-sm text-water-600">
                        <Icon name="plus" className="w-3.5 h-3.5"/> Cena w wariantach / etapach
                      </button>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center gap-2 text-[11px] text-ink-400 mb-1">
                        <span className="w-[70px] text-center">Rozliczany</span>
                        <span className="flex-1">Wariant / etap (z warunkiem z oferty)</span>
                        <span className="w-[140px]">Netto (PLN)</span>
                        <span className="w-8"/>
                      </div>
                      <div className="grid gap-2">
                        {variants.map((r, i) => (
                          <div key={i} className="flex items-center gap-2">
                            <span className="w-[70px] flex justify-center">
                              <input type="checkbox" checked={r.billed}
                                     onChange={e => setVariant(i, { billed: e.target.checked })}
                                     title="Rozliczany — wchodzi do wartości zlecenia"
                                     aria-label={`Wariant ${i + 1} rozliczany`}/>
                            </span>
                            <input value={r.label} onChange={e => setVariant(i, { label: e.target.value })}
                                   className="input flex-1" maxLength={300} aria-label={`Opis wariantu ${i + 1}`}
                                   placeholder={VARIANT_HINTS[Math.min(i, VARIANT_HINTS.length - 1)]}/>
                            <input type="number" step="0.01" min="0" value={r.netAmount}
                                   onChange={e => setVariant(i, { netAmount: e.target.value })}
                                   className="input font-mono w-[140px]" placeholder="0.00"
                                   aria-label={`Kwota netto wariantu ${i + 1}`}/>
                            <button type="button" onClick={() => removeVariant(i)} title="Usuń wariant"
                                    className="btn btn-ghost btn-icon w-8 text-ink-400 hover:text-red-600">
                              <Icon name="x" className="w-3.5 h-3.5"/>
                            </button>
                          </div>
                        ))}
                      </div>
                      <div className="flex items-center justify-between gap-3 mt-2">
                        <button type="button" className="btn btn-secondary btn-sm"
                                onClick={() => setVariants(rows => [...rows, { label: '', netAmount: '', billed: false }])}>
                          <Icon name="plus" className="w-3.5 h-3.5"/> Dodaj wariant
                        </button>
                        <span className="text-[13px] text-ink-600">
                          Wartość zlecenia:{' '}
                          <strong className="font-mono text-ink-900">
                            {billedTotal == null ? 'nieustalona' : formatCurrency(billedTotal)}
                          </strong>
                        </span>
                      </div>
                      <span className="text-[11px] text-ink-400 mt-1">
                        Zaznacz etapy, które rozliczamy — wartość zlecenia to ich suma. Jeśli oferta podaje
                        cenę etapu II razem z etapem I, zaznacz tylko etap II.
                      </span>
                    </>
                  )}
                </div>
              )}

              <label className="field col-span-2">
                <span className="field-label">Ustalenia / zakres</span>
                <textarea value={data.description ?? ''} onChange={set('description')} className="textarea" rows={3}
                          placeholder="Co ustaliliśmy przez telefon — zakres, warunki, kto dzwonił"/>
              </label>
              <label className="field col-span-2">
                <span className="field-label">Uwagi</span>
                <textarea value={data.notes ?? ''} onChange={set('notes')} className="textarea" rows={2}/>
              </label>
            </>
          )}

          {/* ── Prace terenowe od razu ze zleceniem ─────────────────────────────────
              Opcjonalne: zlecenie z telefonu dalej wystarczy zarejestrować samym klientem
              i opisem. Kto zna już studnie, zakłada prace i stawia je na mapie w jednym kroku. */}
          {!initial && canAddWorks && (
            <div className="col-span-2 border-t border-ink-100 pt-3 mt-1">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="field-label">Prace terenowe</div>
                  <div className="text-[12px] text-ink-400">
                    {works.length === 0
                      ? 'Opcjonalnie — prace można też dodać później z karty zlecenia.'
                      : 'Wybierz pracę i kliknij na mapie, gdzie stoi studnia. Pinezkę można przeciągnąć. '
                        + 'Bez punktu praca stanie na mapie w punkcie klienta.'}
                  </div>
                </div>
                <button type="button" onClick={addWork} className="btn btn-secondary btn-sm shrink-0">
                  <Icon name="plus" className="w-3.5 h-3.5"/> Dodaj pracę
                </button>
              </div>

              {works.length > 0 && (
                <>
                  <div className="grid gap-2 mt-3">
                    {works.map((w, i) => {
                      const active = i === activeWork;
                      return (
                        <div key={i} onClick={() => setActiveWork(i)}
                             className={`rounded border p-2.5 grid grid-cols-[28px_1fr_auto] gap-2 cursor-pointer ${
                               active ? 'border-water-500 bg-water-50/40' : 'border-ink-100 hover:border-ink-200'}`}>
                          <span className={`w-6 h-6 rounded-full grid place-items-center text-[11px] font-bold text-white mt-1 ${
                            active ? 'bg-blue-600' : 'bg-teal-700'}`}>{i + 1}</span>
                          <div className="grid grid-cols-2 gap-2 min-w-0">
                            <input value={w.title} onChange={e => setWork(i, { title: e.target.value })}
                                   className="input col-span-2" aria-label={`Opis pracy ${i + 1}`}
                                   placeholder="Opis prac *, np. Regeneracja studni nr 2"/>
                            <SearchableSelect value={w.kind}
                                              onChange={v => setWork(i, { kind: v as FieldWorkKindCode })}>
                              {(workMeta?.kinds ?? [{ id: 'REGENERACJA' as FieldWorkKindCode, name: 'Regeneracja' }])
                                .map(k => <option key={k.id} value={k.id}>{k.name}</option>)}
                            </SearchableSelect>
                            <input type="date" value={w.plannedDate} aria-label={`Planowany termin pracy ${i + 1}`}
                                   title="Planowany termin rozpoczęcia"
                                   onChange={e => setWork(i, { plannedDate: e.target.value })} className="input"/>
                            <input value={w.wellLabel} onChange={e => setWork(i, { wellLabel: e.target.value })}
                                   className="input" placeholder="Studnia, np. SW6/65"
                                   aria-label={`Studnia pracy ${i + 1}`}/>
                            <input value={w.locationText} onChange={e => setWork(i, { locationText: e.target.value })}
                                   className="input" placeholder="Miejscowość / ujęcie"
                                   aria-label={`Miejscowość pracy ${i + 1}`}/>
                            <div className="col-span-2 flex items-center gap-2 text-[12px]">
                              <Icon name="map-pin" className={`w-3.5 h-3.5 ${w.latitude != null ? 'text-teal-700' : 'text-ink-300'}`}/>
                              {w.latitude != null && w.longitude != null ? (
                                <>
                                  <span className="font-mono text-ink-600">{w.latitude.toFixed(6)}, {w.longitude.toFixed(6)}</span>
                                  <button type="button" className="text-ink-400 hover:text-red-600 underline"
                                          onClick={e => { e.stopPropagation(); setWork(i, { latitude: null, longitude: null }); }}>
                                    usuń punkt
                                  </button>
                                </>
                              ) : (
                                <span className="text-ink-400">
                                  {active ? 'Kliknij na mapie poniżej, żeby wskazać studnię' : 'Bez punktu — kliknij wiersz, potem mapę'}
                                </span>
                              )}
                            </div>
                          </div>
                          <button type="button" title="Usuń pracę"
                                  onClick={e => { e.stopPropagation(); removeWork(i); }}
                                  className="btn btn-ghost btn-icon w-8 text-ink-400 hover:text-red-600">
                            <Icon name="x" className="w-3.5 h-3.5"/>
                          </button>
                        </div>
                      );
                    })}
                  </div>

                  <div className="mt-3">
                    <PunktyPracMapa points={mapPoints} activeIndex={activeWork} clientPoint={clientPoint}
                                    onSelect={setActiveWork}
                                    onPick={(i, lat, lng) => setWork(i, { latitude: lat, longitude: lng })}/>
                  </div>

                  {/* Współrzędne z dokumentów (WGS84 albo PUWG 2000) — dla aktywnej pracy. */}
                  {activeWork != null && works[activeWork] && (
                    <div className="mt-3 grid grid-cols-2 gap-3">
                      <div className="col-span-2 text-[12px] text-ink-500">
                        Albo wpisz współrzędne pracy {activeWork + 1} ręcznie:
                      </div>
                      <CoordsInput key={activeWork}
                                   latitude={works[activeWork].latitude}
                                   longitude={works[activeWork].longitude}
                                   onChange={(lat, lng) => setWork(activeWork, { latitude: lat, longitude: lng })}
                                   layout="stacked"
                                   label="Współrzędne studni"/>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {err && <div className="col-span-2 text-red-600 text-[12px]">Błąd: {err}</div>}
        </div>

        <div className="px-5 py-3 border-t border-ink-100 flex justify-end gap-2 sticky bottom-0 bg-white">
          <button type="button" onClick={requestClose} className="btn btn-secondary">Anuluj</button>
          <button type="submit" disabled={busy} className="btn btn-primary">
            {busy ? 'Zapisywanie…' : (initial ? 'Zapisz zmiany' : 'Zarejestruj zlecenie')}
          </button>
        </div>
      </form>
    </div>
  );
}
